import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Shield, CheckCircle2, Clock, FileCheck, Bell, BarChart3, Download, Star,
  Users, CreditCard, AlertTriangle, ArrowDown, ClipboardCheck, MessageSquareText,
  FolderOpen, ScrollText, Zap, ArrowRight, CalendarSync,
} from "lucide-react";

const cascadeSteps = [
  { label: "Missed CEs", icon: AlertTriangle, color: "border-status-expiring bg-status-expiring/10 text-status-expiring" },
  { label: "BHEC Lapses", icon: Shield, color: "border-status-expired bg-status-expired/10 text-status-expired" },
  { label: "Medicare Lost", icon: Zap, color: "border-destructive bg-destructive/10 text-destructive" },
  { label: "Medicaid Lost", icon: Zap, color: "border-destructive bg-destructive/10 text-destructive" },
  { label: "MCO Panels Gone", icon: AlertTriangle, color: "border-destructive bg-destructive/10 text-destructive" },
];

const features = [
  { icon: FileCheck, title: "DIY Credentialing Guide", desc: "12-step wizard — NPI → CAQH → PECOS → TMHP → MCOs. Correct order, every portal linked.", tag: "Solo LPCs" },
  { icon: Clock, title: "Renewal Tracker", desc: "29 credentials with auto-calculated due dates and color-coded urgency.", tag: "All LPCs" },
  { icon: ClipboardCheck, title: "Oversight Checklist", desc: "59 tasks across 8 sections to verify your employer's credentialing work.", tag: "Employed LPCs" },
  { icon: MessageSquareText, title: "Employer Q&A", desc: "12 pre-written questions with date and answer logging.", tag: "Employed LPCs" },
  { icon: Bell, title: "SMS & Email Alerts", desc: "Automated reminders at 90, 60, 30, and 7 days before each deadline.", tag: "Coming Soon" },
  { icon: BarChart3, title: "Completeness Score", desc: "Visual progress across all 29 Medicare & Medicaid credentials.", tag: "All LPCs" },
  { icon: FolderOpen, title: "Document Library", desc: "Secure storage linked to the credentials they support.", tag: "All LPCs" },
  { icon: ScrollText, title: "Action Log", desc: "Timestamped audit trail. Export as PDF for compliance.", tag: "All LPCs" },
  { icon: CalendarSync, title: "Calendar Sync", desc: "Export renewal deadlines as .ics and sync with Google Calendar, Outlook, or Apple Calendar.", tag: "Coming Soon" },
  { icon: Download, title: "Data Export", desc: "Full data portability. Your data, your control.", tag: "All LPCs" },
];

const testimonials = [
  { name: "Sarah M., LPC", quote: "I was drowning in Medicare paperwork. CredentialEdge made it manageable in one afternoon.", stars: 5 },
  { name: "James R., LPC-S", quote: "The renewal reminders saved me from two near-misses with CAQH re-attestation. Worth every penny.", stars: 5 },
  { name: "Dr. Kim L., LPC", quote: "Finally — a tool built for counselors navigating Medicare and Medicaid, not doctors. It just gets it.", stars: 5 },
];

const faqs = [
  { q: "Do I need this if my employer handles credentialing?", a: "Your employer manages the process — but your name is on every Medicare and Medicaid claim they submit. If their credentialing is wrong, you face the consequences. CredentialEdge helps you verify their work and document that verification." },
  { q: "I'm not billing Medicare yet. Do I still need this?", a: "If you hold an LPC license in Texas, the 2024 Medicare expansion affects you even if you haven't enrolled. Depending on your situation, you may need to actively opt out — or face unexpected obligations." },
  { q: "What if I'm in a county other than Bexar?", a: "Federal Medicare and Texas state Medicaid credentials are supported regardless of county. Bexar County MCOs are fully covered at launch, with the 10 highest-population Texas counties coming in 2026." },
  { q: "Is my data secure?", a: "Yes. Encrypted in transit and at rest. Export a full backup anytime from account settings." },
  { q: "What is your cancellation policy?", a: "Cancel anytime — no questions, no fees. During your 14-day trial you're never charged. Paid plans retain access through the end of the billing period." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b bg-card/80 backdrop-blur sticky top-0 z-40">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <div className="flex flex-col leading-none">
              <span className="font-display font-bold text-lg"><span className="text-primary">Credential</span><span className="text-foreground">Edge</span></span>
              <span className="text-[9px] text-muted-foreground tracking-wide">Credentialing Made Simple</span>
            </div>
            <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 h-4 border-primary/40 text-primary font-semibold">BETA</Badge>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild><Link to="/login">Sign In</Link></Button>
            <Button size="sm" asChild><Link to="/signup">Join the Beta</Link></Button>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="container py-20 md:py-28 max-w-4xl mx-auto">
        <div className="text-center">
          <Badge variant="secondary" className="mb-4">🚀 Beta Testing Period — Invite Only</Badge>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
            Credentialing<br />
            <span className="text-primary">Made Simple</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Get credentialed with Medicare & Medicaid for the first time — or track your renewals if you already are. One platform covering BHEC, CAQH, PECOS, TMHP, and Bexar County MCOs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
            <Button size="lg" className="text-base px-8" asChild>
              <Link to="/signup">Join the Beta</Link>
            </Button>
            <Button size="lg" variant="outline" className="text-base" asChild>
              <Link to="#pricing">View Pricing</Link>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5">
            Invite code required · Free during beta · No credit card needed
          </p>
        </div>
      </section>

      {/* ═══ CREDENTIAL CASCADE ═══ */}
      <section className="border-y bg-muted/40 py-16">
        <div className="container max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold text-status-expired uppercase tracking-wider mb-2">The Credential Cascade</p>
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">One Missed Deadline Can Shut Down Your Entire Billing Operation</h2>
          <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
            Your credentials are more connected than you think. Here's what happens when one lapses:
          </p>

          {/* Visual cascade */}
          <div className="flex flex-col items-center gap-3 mb-8">
            {cascadeSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.label} className="flex flex-col items-center">
                  <div className={`border-2 ${step.color} rounded-xl px-6 py-3 flex items-center gap-3 min-w-[220px]`}>
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-semibold">{step.label}</span>
                  </div>
                  {i < cascadeSteps.length - 1 && (
                    <ArrowDown className="h-4 w-4 text-muted-foreground mt-1" />
                  )}
                </div>
              );
            })}
          </div>

          <p className="font-medium text-foreground text-lg">
            No spreadsheet can prevent that. <span className="text-primary font-bold">CredentialEdge can.</span>
          </p>
        </div>
      </section>

      {/* ═══ TWO PATHS ═══ */}
      <section className="container py-16 max-w-4xl mx-auto">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-3">Two Paths, One Platform</h2>
        <p className="text-center text-muted-foreground mb-10">Whether you're starting from scratch or already enrolled.</p>
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="border-l-4 border-l-primary hover:shadow-md transition-shadow group">
            <CardContent className="p-6">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                <FileCheck className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">New to Credentialing?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Our 12-step DIY Guide walks you through the entire Medicare & Medicaid enrollment — in the correct order, with links to every portal.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {["NPI", "I&A", "CAQH", "PECOS", "TMHP", "MCOs"].map((s) => (
                  <Badge key={s} variant="outline" className="text-xs font-normal">{s}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-secondary hover:shadow-md transition-shadow group">
            <CardContent className="p-6">
              <div className="h-12 w-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-4 group-hover:bg-secondary/15 transition-colors">
                <Clock className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">Already Credentialed?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Track 29 Medicare & Medicaid credentials with auto-calculating due dates and color-coded urgency.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {["Federal", "State", "County MCOs", "Medicare Advantage"].map((s) => (
                  <Badge key={s} variant="outline" className="text-xs font-normal">{s}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="bg-muted/40 py-16">
        <div className="container">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-3">Built for Medicare & Medicaid Compliance</h2>
          <p className="text-center text-muted-foreground mb-12">Every tool a Texas LPC needs — nothing you don't.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="border-0 shadow-sm hover:shadow-md transition-shadow group">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-[10px] font-normal">{f.tag}</Badge>
                    </div>
                    <h3 className="font-display font-semibold mb-1">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ WHO IT'S FOR ═══ */}
      <section className="container py-16 max-w-4xl mx-auto">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-10">Who It's Built For</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {[
            { icon: Shield, title: "Employed LPCs", desc: "Verify your employer's credentialing work and keep documented proof." },
            { icon: ArrowRight, title: "Solo & Independent LPCs", desc: "Navigate Medicare & Medicaid credentialing for the first time — step by step." },
            { icon: Users, title: "Group Practice Owners", desc: "Track renewals for multiple providers with admin oversight tools." },
            { icon: CheckCircle2, title: "Any Texas LPC", desc: "If you can't afford a billing gap or compliance failure — this is for you." },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.title} className="flex gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold mb-0.5">{a.title}</h3>
                  <p className="text-sm text-muted-foreground">{a.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="bg-muted/40 py-16">
        <div className="container">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-10">What Counselors Are Saying</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <Card key={t.name} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-status-expiring text-status-expiring" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground mb-4 italic leading-relaxed">"{t.quote}"</p>
                  <p className="text-sm font-semibold text-muted-foreground">{t.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="container py-16 max-w-5xl mx-auto">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-2">Simple, Transparent Pricing</h2>
        <p className="text-center text-muted-foreground mb-1">14-day free trial on all plans. Cancel anytime.</p>
        <p className="text-center text-sm text-muted-foreground mb-10">Less than one copay per month.</p>

        <h3 className="font-display text-lg font-semibold text-center mb-5">Solo Practitioner</h3>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
          <Card className="border shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center">
              <h4 className="font-display font-semibold text-lg mb-1">Monthly</h4>
              <div className="text-4xl font-bold mb-1">$19<span className="text-lg text-muted-foreground font-normal">/mo</span></div>
              <p className="text-sm text-muted-foreground mb-4">Billed monthly</p>
              <Button variant="outline" className="w-full" asChild><Link to="/signup">Start Free Trial</Link></Button>
            </CardContent>
          </Card>
          <Card className="border-2 border-primary shadow-md relative hover:shadow-lg transition-shadow">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">BEST VALUE</Badge>
            <CardContent className="pt-6 text-center">
              <h4 className="font-display font-semibold text-lg mb-1">Annual</h4>
              <div className="text-4xl font-bold mb-1">$190<span className="text-lg text-muted-foreground font-normal">/yr</span></div>
              <p className="text-sm text-muted-foreground mb-1">$15.83/mo — 2 months free</p>
              <p className="text-xs text-secondary font-medium mb-4">Save $38/year</p>
              <Button className="w-full" asChild><Link to="/signup">Start Free Trial</Link></Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mb-5">
          <h3 className="font-display text-lg font-semibold flex items-center justify-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Group Practice
          </h3>
          <p className="text-sm text-muted-foreground">Platform fee + per-LPC pricing · Up to 25 LPCs</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <Card className="border shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center space-y-2">
              <h4 className="font-semibold text-lg">Monthly</h4>
              <div className="text-3xl font-bold">$19<span className="text-sm text-muted-foreground font-normal">/mo</span></div>
              <p className="text-lg font-semibold text-primary">+ $10<span className="text-sm text-muted-foreground font-normal">/LPC/mo</span></p>
              <Button variant="outline" className="w-full" asChild><Link to="/signup">Start Free Trial</Link></Button>
            </CardContent>
          </Card>
          <Card className="border-2 border-primary shadow-md relative hover:shadow-lg transition-shadow">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">2 MONTHS FREE</Badge>
            <CardContent className="pt-6 text-center space-y-2">
              <h4 className="font-semibold text-lg">Annual</h4>
              <div className="text-3xl font-bold">$190<span className="text-sm text-muted-foreground font-normal">/yr</span></div>
              <p className="text-lg font-semibold text-primary">+ $120<span className="text-sm text-muted-foreground font-normal">/LPC/yr</span></p>
              <p className="text-xs text-secondary font-medium">Up to 25 LPCs</p>
              <Button className="w-full" asChild><Link to="/signup">Start Free Trial</Link></Button>
            </CardContent>
          </Card>
        </div>
        <p className="text-center text-sm text-muted-foreground mt-4">
          Need more than 25 LPCs? <Link to="/signup" className="text-primary hover:underline">Let's talk</Link>.
        </p>
        <p className="text-center text-sm text-muted-foreground mt-6 flex items-center justify-center gap-1.5">
          <CreditCard className="h-3.5 w-3.5" /> Credit card required to start trial · Cancel anytime — no charge
        </p>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="bg-muted/40 py-16">
        <div className="container max-w-2xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-sm">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20">
        <div className="container text-center max-w-2xl mx-auto">
          <Shield className="h-10 w-10 text-primary mx-auto mb-4" />
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
            Your Medicare & Medicaid credentials are too important for a spreadsheet.
          </h2>
          <p className="text-muted-foreground mb-6">Set up in minutes. First renewal dashboard running before your next client session.</p>
          <Button size="lg" className="text-base px-8" asChild>
            <Link to="/signup">Start Your Free 14-Day Trial</Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <div className="flex flex-col leading-none">
              <span className="font-display font-bold text-sm"><span className="text-primary">Credential</span><span className="text-foreground">Edge</span></span>
              <span className="text-[8px] text-muted-foreground tracking-wide">Credentialing Made Simple</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-foreground underline">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground underline">Privacy</Link>
            <Link to="/help" className="hover:text-foreground underline">Help & FAQ</Link>
            <span>© {new Date().getFullYear()} CredentialEdge</span>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">A product of Corley Legal. Built in Texas, for Texas LPCs.</p>
      </footer>
    </div>
  );
}
